import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  BarChart2, Map, FileText, Users, Settings, DollarSign, 
  ClipboardList, CalendarDays, Menu, X, Database,
  ChevronDown, ChevronRight, MapPin, Beaker
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  show?: boolean;
  children?: NavItem[];
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const { canManageSystemSettings, canViewAuditLogs } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { pathname } = useLocation();

  const toggleExpanded = (path: string) => {
    setExpandedItems(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [path] // Only allow one menu to be expanded at a time
    );
  };

  // Keep menus expanded if we're on their pages
  React.useEffect(() => {
    if (pathname.startsWith('/admin')) {
      setExpandedItems(['/admin']);
    } else if (pathname.startsWith('/enhanced')) {
      setExpandedItems(['/enhanced']);
    }
  }, [pathname]);

  const navItems: NavItem[] = [
    // Test Page - hidden from all users
    { icon: Beaker, label: 'Test Page', path: '/test-page', show: false },
    // Add back Dashboard and Map
    { icon: BarChart2, label: 'Dashboard', path: '/' },
    { icon: Map, label: 'Map', path: '/map' },
    // Enhanced items moved to top level (without User-Management)
    { icon: FileText, label: 'Lots', path: '/enhanced/lots' },
    { icon: FileText, label: 'Sub-Leases', path: '/enhanced/sub-leases' },
    { icon: FileText, label: 'Under-Leases', path: '/enhanced/under-leases' },
    { icon: Users, label: 'Leaseholders', path: '/enhanced/leaseholders' },
    { icon: ClipboardList, label: 'Applicants', path: '/enhanced/applicants' },
    { icon: ClipboardList, label: 'Applications', path: '/enhanced/applications' },
    { icon: DollarSign, label: 'Rental-Contracts', path: '/enhanced/rental-contracts' },
    { icon: DollarSign, label: 'Financials', path: '/enhanced/financials' },
    { icon: CalendarDays, label: 'Meetings', path: '/enhanced/meetings' },
    { icon: ClipboardList, label: 'Market Reviews', path: '/enhanced/market-reviews' },
    { icon: FileText, label: 'Lease EOIs', path: '/enhanced/lease-eois' },
    { icon: FileText, label: 'EOI-Responses', path: '/enhanced/eoi-responses' },
    { icon: FileText, label: 'Projects', path: '/enhanced/projects' },
    { icon: FileText, label: 'Suppliers', path: '/enhanced/suppliers' },
    { icon: ClipboardList, label: 'Audit-Logs', path: '/enhanced/audit-logs', show: canViewAuditLogs },
    { icon: Settings, label: 'User Admin', path: '/admin/settings/user-management', show: canManageSystemSettings }
  ];

  const renderNavItem = (item: NavItem) => {
    // Skip rendering if show is explicitly false
    if (item.show === false) return null;
    
    const isExpanded = expandedItems.includes(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isActive = pathname === item.path || 
      (hasChildren && item.children?.some(child => pathname === child.path));

    return (
      <li key={item.path}>
        {hasChildren ? (
          <div className="space-y-1">
            <button
              onClick={() => toggleExpanded(item.path)}
              className={`w-full flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <item.icon className="mr-3 h-5 w-5" />
              {item.label}
              {isExpanded ? (
                <ChevronDown className="ml-auto h-4 w-4" />
              ) : (
                <ChevronRight className="ml-auto h-4 w-4" />
              )}
            </button>
            {isExpanded && (
              <ul className="pl-4 space-y-1">
                {item.children?.map(child => (
                  <li key={child.path}>
                    <NavLink
                      to={child.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      <child.icon className="mr-3 h-5 w-5" />
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <NavLink
            to={item.path}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`
            }
          >
            <item.icon className="mr-3 h-5 w-5" />
            {item.label}
          </NavLink>
        )}
      </li>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] lg:hidden">
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
      
      <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white mt-20">
        <div className="h-16 px-4 flex items-center justify-between border-b">
          <span className="text-lg font-semibold text-primary">Menu</span>
          <button onClick={onClose} className="p-2 rounded-md text-gray-500 hover:bg-gray-100">
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map(renderNavItem)}
          </ul>
        </nav>
      </div>
    </div>
  );
}
